import React, { useEffect, useState } from 'react'
import RegistrationComponent from '../Components/RegistrationComponent'
import { useNavigate, useParams } from 'react-router-dom';
import { validateInviteCode } from '../../Admin/Services/AdminService';
import { getPublicInvitation } from '../../Admin/Services/GrowthService';
import { AccessTrack, TargetRole } from '../../Admin/Types/GrowthType';
import { Alert, AlertProps } from '../../Shared/Utils/Alert';

type LoadStatus = 'loading' | 'success' | 'error';

export interface InviteContext {
  /** The only role this invite may register as, or null when the portal does not lock it. */
  targetRole: TargetRole | null;
  accessTrack: AccessTrack | null;
  waitlistStatus: string | null;
  accessLabel: string | null;
}

const asTargetRole = (value?: string | null): TargetRole | null => {
  if (!value) return null;
  const normalized = value.trim().toUpperCase();
  return normalized === 'CANDIDATE' || normalized === 'RECRUITER' ? normalized : null;
};

function RegistrationPage() {

   const { inviteCode } = useParams<{ inviteCode: string }>();
  const navigate = useNavigate();
  const [status, setStatus] = useState<LoadStatus>('loading');
  const [alert, setAlert] = useState<AlertProps | null>(null);
  const [invite, setInvite] = useState<InviteContext>({
    targetRole: null,
    accessTrack: null,
    waitlistStatus: null,
    accessLabel: null,
  });

  useEffect(() => {
    const checkInvite = async () => {
      if (!inviteCode) {
        setStatus('error');
        setAlert({
          variant: "error",
          title: "Registration Error",
          message: "Invalid or missing invitation code. Please contact support for assistance.",
          autoCloseMs: 5000,
          onClose: () => setAlert(null),
        });
        return;
      }

      // Admin-issued growth invitations carry the single role + waitlist track they grant.
      const growth = await getPublicInvitation(inviteCode);
      if (growth.success && growth.data && !growth.data.expired) {
        setInvite({
          targetRole: growth.data.targetRole,
          accessTrack: growth.data.accessTrack,
          waitlistStatus: growth.data.waitlistStatus,
          accessLabel: growth.data.accessLabel,
        });
        setStatus('success');
        return;
      }

      // Member-to-member private invitations fall back to the inviter's role.
      const response = await validateInviteCode(inviteCode);
      if (response.success && response.data) {
        setInvite({
          targetRole: asTargetRole(response.data.userRole),
          accessTrack: null,
          waitlistStatus: null,
          accessLabel: null,
        });
        setStatus('success');
        return;
      }

      setStatus('error');
      setAlert({
        variant: "error",
        title: "Registration Error",
        message: response.message || growth.message || "This invitation is invalid or has expired.",
        autoCloseMs: 5000,
        onClose: () => setAlert(null),
      });
      setTimeout(() => navigate('/login'), 2000);
    };

    checkInvite();
  }, [inviteCode, navigate]);


return (
  <div>


    {alert && (
      <div className="mb-6">
        <Alert {...alert} onClose={() => setAlert(null)} />
      </div>
    )}

    {status === 'loading' && (
      <div>Validating...</div>
    )}

    {status === 'error' && (
      <div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">
        Validating your invitation&hellip;
      </div>
    )}

    {status === 'success' && (
      <RegistrationComponent invite={invite} />
    )}

  </div>
);
}

export default RegistrationPage
